import { useToast } from '@/components/ui/Toast/ToastProvider';
import {
  useAccessRequests,
  useApproveRequest,
  useDeclineRequest,
} from '@/services/accessRequests';
import { Button } from '@/components/ui/Button/Button';
import { Icon } from '@/components/ui/Icon/Icon';
import { nb } from '@/i18n/nb';
import type { AccessRequest } from '@/types/api';
import s from '../admin.module.css';

export function AdminAccessRequests() {
  const toast = useToast();
  const { data: requests = [] } = useAccessRequests();
  const approve = useApproveRequest();
  const decline = useDeclineRequest();

  function toastError(error: unknown) {
    toast({
      title: nb.admin.toast.errorTitle,
      body: error instanceof Error ? error.message : undefined,
      error: true,
    });
  }

  function onApprove(request: AccessRequest) {
    approve.mutate(request.id, {
      onSuccess: () =>
        toast({
          title: nb.admin.toast.approvedTitle,
          body: nb.admin.toast.approvedBody.replace('{navn}', request.name),
        }),
      onError: toastError,
    });
  }

  function onDecline(request: AccessRequest) {
    decline.mutate(request.id, {
      onSuccess: () =>
        toast({ title: nb.admin.toast.declinedTitle, body: nb.admin.toast.declinedBody }),
      onError: toastError,
    });
  }

  return (
    <div>
      <div className={s.head}>
        <div>
          <h2 className={s.title}>{nb.admin.accessRequests.title}</h2>
          <p className={s.lede}>{nb.admin.accessRequests.lede}</p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className={s.empty}>
          <Icon name="check" size={34} />
          <p>{nb.admin.accessRequests.empty}</p>
        </div>
      ) : (
        <div className={s.cards}>
          {requests.map((request) => (
            <div key={request.id} className={s.reqCard}>
              <div className={s.reqInfo}>
                <b>{request.name}</b>
                <span className={s.cellMuted}>{request.email}</span>
                <span className={s.cellMuted}>
                  {nb.admin.accessRequests.hostPrefix}: {request.host}
                </span>
                <span className={s.cellMuted}>
                  {nb.admin.accessRequests.sentPrefix} {formatDate(request.submittedAt)}
                </span>
              </div>
              <div className={s.reqActs}>
                <Button variant="secondary" onClick={() => onDecline(request)}>
                  {nb.admin.accessRequests.decline}
                </Button>
                <Button variant="primary" onClick={() => onApprove(request)}>
                  {nb.admin.accessRequests.approve}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}
